import { ConfirmPackingResponse, PackingApiAdapter } from '../adapters';
import { Order, PreparationWithProducts } from '../models';
import { packingScanCodesMatch } from '../utils/packingAssignKey';

export class PackingService {
  // Hacer el adaptador público para acceso desde hooks
  public adapter: PackingApiAdapter;

  constructor(adapter: PackingApiAdapter) {
    this.adapter = adapter;
  }

  /**
   * Obtiene todas las preparaciones de packing con sus productos
   */
  async getAllPreparations(): Promise<PreparationWithProducts[]> {
    return await this.adapter.getAllPreparationsWithProducts();
  }

  /**
   * Obtiene una preparación específica con sus productos
   */
  async getPreparation(id: number): Promise<PreparationWithProducts> {
    return await this.adapter.getPreparationWithProducts(id);
  }

  /**
   * Confirma el empaquetado de un producto
   */
  async confirmPacking(preparationId: number, lineId: number, quantity: number): Promise<ConfirmPackingResponse> {
    return await this.adapter.confirmPacking(preparationId, lineId, quantity);
  }

  /**
   * Busca preparaciones por código
   */
  async searchPreparationByCode(
    preparations: PreparationWithProducts[],
    code: string,
  ): PreparationWithProducts | undefined {
    return preparations.find((prep) => packingScanCodesMatch(prep.code, code));
  }

  /**
   * Verifica si una línea está completada usando la misma lógica que el Packing original
   */
  private isLineCompleted(line: any, orders: Order[] = [], preparationId?: number): boolean {
    // Función para obtener assignedQuantities del localStorage
    const getAssignedQuantities = () => {
      try {
        const routeKey = `packing:assigned:${preparationId || 'unknown'}`;
        const assigned: Record<string, number> = JSON.parse(localStorage.getItem(routeKey) || '{}');
        return assigned;
      } catch {
        return {};
      }
    };

    // Cantidad completada proveniente de pedidos terminados (con processed_at)
    const completedQuantityFromFinishedOrders = orders.reduce((total, order) => {
      if (!order.shipment?.processed_at) return total;

      let orderCompletedQty = 0;
      (order.lines || []).forEach((orderLine) => {
        if (orderLine.pack && orderLine.pack.lines) {
          // Es un pack - multiplicar cantidad del producto × cantidad de packs
          const packQuantity = orderLine.qty || 1;
          orderLine.pack.lines.forEach((packLine) => {
            if (packingScanCodesMatch(packLine.product?.code, line.product?.code)) {
              orderCompletedQty += (packLine.qty || 0) * packQuantity;
            }
          });
        } else if (orderLine.product && packingScanCodesMatch(orderLine.product.code, line.product?.code)) {
          // Es un producto individual
          orderCompletedQty += orderLine.qty || 0;
        }
      });

      return total + orderCompletedQty;
    }, 0);

    // Cantidad asignada actualmente (localStorage)
    const assignedQuantitiesMap = getAssignedQuantities();
    const assignedForProduct = Object.entries(assignedQuantitiesMap)
      .filter(([key]) => {
        const parts = key.split(':');
        // Key: orderId:lineId:productCode (líneas sueltas y productos dentro de packs)
        if (parts.length === 3) {
          return packingScanCodesMatch(parts[2], line.product?.code);
        }
        return false;
      })
      .reduce((sum, [, qty]) => sum + Number(qty), 0);

    // Procesado = completado (órdenes terminadas) + asignado en curso
    const processed = Math.min(completedQuantityFromFinishedOrders + assignedForProduct, line.qty);

    return processed >= line.qty;
  }

  /**
   * Busca producto en una preparación por código usando la misma lógica que el Packing original
   */
  async searchProductInPreparation(
    preparation: PreparationWithProducts,
    productCode: string,
    orders: Order[] = [],
  ): { lineId: number; productName: string } | undefined {
    const foundLine = preparation.lines.find((line) => {
      // Verificar si el código coincide
      const codeMatches =
        packingScanCodesMatch(line.product?.code, productCode) || packingScanCodesMatch(line.product?.ean, productCode);

      if (!codeMatches) return false;

      // Verificar si no está completado usando la misma lógica del Packing original
      return !this.isLineCompleted(line, orders, preparation.id);
    });

    if (foundLine) {
      return {
        lineId: foundLine.id,
        productName: foundLine.product?.name || `Producto ID: ${foundLine.product_id}`,
      };
    }

    return undefined;
  }
}
